/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useRef } from 'react'
import { useServiceContext } from '../../root'
import FsiPicker, { FsiPickerOptions, TreeNode } from '../FsiPicker'

/**
 * FsiPickerModal - Modal wrapper for FsiPicker component
 *
 * This component displays the FsiPicker tree in a Bootstrap modal dialog,
 * matching the legacy AngularJS implementation pattern.
 *
 * @example
 * ```tsx
 * <FsiPickerModal
 *   options={{
 *     selector: 'folder',
 *     showRC: true,
 *     showSystem: true,
 *     selectedId: 123,
 *     leafIds: [123, 456]
 *   }}
 *   title="SELECT_FOLDER"
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   onSelect={(fsi) => console.log('Selected:', fsi)}
 * />
 * ```
 */
export interface FsiPickerModalProps {
  /** Options passed to FsiPicker component */
  options: Partial<FsiPickerOptions>
  /** Translation key for modal title */
  title: string
  /** Controls modal visibility */
  isOpen: boolean
  /** Called when modal is closed */
  onClose: () => void
  /** Called when user confirms selection */
  onSelect: (fsi: { id: string | number; name: string }) => void
  /** Optional callback to filter selectable nodes */
  isNodeSelectable?: (node: TreeNode) => boolean
  /** Optional callback to filter expandable nodes */
  isNodeExpandable?: (node: TreeNode) => boolean
  /** Show add new folder button (currently not implemented) */
  showAddNewFolderModal?: boolean
  /** Additional CSS class for modal */
  modalClassName?: string
  /** Additional CSS class for backdrop */
  backdropClassName?: string
}

const FsiPickerModal: React.FC<FsiPickerModalProps> = ({
  options,
  title,
  isOpen,
  onClose,
  onSelect,
  isNodeSelectable,
  isNodeExpandable,
  showAddNewFolderModal,
  modalClassName = '',
  backdropClassName = ''
}) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const modalRef = useRef<HTMLDivElement>(null)

  // Track selected node
  const [selectedNode, setSelectedNode] = useState<TreeNode | null>(null)

  // Ensure options have defaults - NO radio buttons, NO checkboxes for single selection
  const pickerOptions: Partial<FsiPickerOptions> = {
    ...options,
    showRadio: false, // Never show radio buttons in modal
    showCheckBox: false // Single selection mode for folder picker
  }

  // Add/remove modal-open class on body
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open')
    } else {
      document.body.classList.remove('modal-open')
    }

    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [isOpen])

  // Handle selection from FsiPicker
  // In single-select mode (no checkbox), FsiPicker passes the node object directly
  // In checkbox mode, FsiPicker passes (selectedIds[], baseNodes[])
  const handleFsiPickerSelect = (nodeOrIds: TreeNode | (number | string)[], baseNodes?: TreeNode[]) => {
    // Check if it's a TreeNode object (single-select mode)
    if (typeof nodeOrIds === 'object' && !Array.isArray(nodeOrIds) && 'id' in nodeOrIds && 'name' in nodeOrIds) {
      // Single-select mode: received node object directly
      setSelectedNode(nodeOrIds as TreeNode)
    } else if (Array.isArray(nodeOrIds) && baseNodes) {
      // Checkbox mode: received array of IDs
      const selectedIds = nodeOrIds as (number | string)[]

      if (selectedIds.length > 0) {
        const selectedId = selectedIds[selectedIds.length - 1]

        // Find the selected node in the tree
        const findNode = (nodes: TreeNode[]): TreeNode | null => {
          for (const node of nodes) {
            if (node.id === selectedId) {
              return node
            }
            if (node.children) {
              const found = findNode(node.children)
              if (found) return found
            }
          }
          return null
        }

        const node = findNode(baseNodes)
        if (node) {
          setSelectedNode(node)
        }
      }
    }
  }

  // Handle OK button click
  const handleConfirmSelection = () => {
    if (selectedNode) {
      onSelect({
        id: selectedNode.id,
        name: selectedNode.name || selectedNode.fileName || ''
      })
    }
    handleClose()
  }

  // Handle close
  const handleClose = () => {
    setSelectedNode(null)
    onClose()
  }

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose()
    }
  }

  // Handle ESC key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleClose()
    }
  }

  if (!isOpen) {
    return null
  }

  return (
    <div className="theme-sandman">
      {/* Modal Backdrop */}
      <div className={`modal-backdrop fade in ${backdropClassName}`} onClick={handleBackdropClick} aria-hidden="true" />

      {/* Modal Dialog */}
      <div
        ref={modalRef}
        className={`modal fade in ${modalClassName}`}
        style={{ display: 'block' }}
        tabIndex={-1}
        role="dialog"
        onClick={handleBackdropClick}
        onKeyDown={handleKeyDown}
        aria-labelledby="fsi-picker-modal-title"
      >
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="modal-content">
            {/* Modal Header */}
            <div className="modal-header">
              <button type="button" className="close" onClick={handleClose} aria-label="Close">
                <span aria-hidden="true">&times;</span>
              </button>
              <h4 className="modal-title" id="fsi-picker-modal-title">
                {translate(title)}
              </h4>
            </div>

            {/* Modal Body */}
            <div className="modal-body">
              <FsiPicker
                options={pickerOptions}
                onSelect={handleFsiPickerSelect}
                isNodeSelectable={isNodeSelectable}
                isNodeExpandable={isNodeExpandable}
                showAddNewFolderModal={showAddNewFolderModal}
              />
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-link"
                onClick={handleClose}
                data-action="cancel"
                id="fsi-picker-cancel-btn"
              >
                {translate('CANCEL')}
              </button>
              <button
                type="button"
                className="btn btn-primary text-capitalize"
                onClick={handleConfirmSelection}
                data-action="ok"
                id="fsi-picker-ok-btn"
              >
                {translate('OK')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default FsiPickerModal
